import { useCallback, useEffect, useState } from "react";
import { Search } from "react-bootstrap-table2-toolkit";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";

import {
  Box,
  Button,
  Container,
  Card,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";

// @material-ui/lab components
import { Skeleton } from "@material-ui/lab";

// @material-ui/icons components
import {
  Create,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from "@material-ui/icons";

// core components
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import FormIntegrationDialog from "components/Dialogs/FormIntegrationDialog";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import { columnTextField, columnDateField } from "utils/tables-utils";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import useLogs from "hooks/logs";
import { IsMobile } from "utils/is-mobile";

const getTableColumns = (
  setSelectedIntegration,
  toggleFormDialog,
  searching
) => [
  columnTextField(
    {
      dataField: "NomeMarca",
      text: "NomeMarca",
      formatter: (value) => (searching ? <Skeleton variant="text" /> : value),
      align: "center",
    },
    true
  ),
  columnTextField(
    {
      dataField: "NomeSistema",
      text: "NomeSistema",
      formatter: (value) => (searching ? <Skeleton variant="text" /> : value),
      align: "center",
    },
    true
  ),
  columnTextField(
    {
      dataField: "Chave",
      text: "Chave",
      formatter: (value) => (searching ? <Skeleton variant="text" /> : value),
    },
    true
  ),
  columnTextField(
    {
      dataField: "Valor",
      text: "Valor",
      formatter: (value) => (searching ? <Skeleton variant="text" /> : value),
    },
    true
  ),
  columnTextField(
    {
      dataField: "InformacaoPublica",
      text: "Informação Pública",
      align: "center",
      formatter: (value) =>
        searching ? (
          <Skeleton variant="text" />
        ) : (
          MaskAndValid.formatYesNo(value)
        ),
    },
    true
  ),
  columnDateField(
    {
      dataField: "DataHoraAlteracao",
      text: "Data/Hora Alteração ",
      formatter: (value) =>
        searching ? (
          <Skeleton variant="text" />
        ) : (
          MaskAndValid.formattDateHourBR(value)
        ),
    },
    true
  ),
  {
    dataField: "editar",
    text: "",
    isDummyField: true,
    csvExport: false,
    headerStyle: { width: 45 },
    formatter: (cell, row) => {
      return searching ? null : (
        <Tooltip title="Editar Integração">
          <IconButton
            aria-label="edit"
            onClick={() => {
              setSelectedIntegration(row);
              toggleFormDialog("edit");
            }}
          >
            <Box
              component={Create}
              width="1.25rem!important"
              height="1.25rem!important"
            />
          </IconButton>
        </Tooltip>
      );
    },
  },
];

const loadingItems = [
  { IdIntegracaoConfiguracao: "0" },
  { IdIntegracaoConfiguracao: "1" },
  { IdIntegracaoConfiguracao: "2" },
  { IdIntegracaoConfiguracao: "3" },
  { IdIntegracaoConfiguracao: "4" },
  { IdIntegracaoConfiguracao: "5" },
  { IdIntegracaoConfiguracao: "6" },
  { IdIntegracaoConfiguracao: "7" },
  { IdIntegracaoConfiguracao: "8" },
  { IdIntegracaoConfiguracao: "9" },
];

const { SearchBar } = Search;

const useStyles = makeStyles(componentStyles);

function ConfiguracaoIntegracoes() {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { setLocalStorageActions, setLocalStorageApiProblem } = useLogs();

  const [integrations, setIntegrations] = useState(loadingItems);
  const [systemIntegrations, setSystemIntegrations] = useState([]);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [searching, setSearching] = useState(true);
  const isMobile = IsMobile();

  const toggleFormDialog = useCallback((formType) => {
    if (formType) {
      setOpenFormDialog(formType);
    }
  }, []);

  const updateList = useCallback(
    async (openFormDialog, updatedMenu) => {
      if (openFormDialog === "delete") {
        const filteredIntegration = integrations.filter(
          (integration) =>
            integration.IdIntegracaoConfiguracao !==
            updatedMenu.IdIntegracaoConfiguracao
        );

        setIntegrations(filteredIntegration);
      } else {
        const newMenuList = integrations?.map((integration) => {
          if (
            integration.IdIntegracaoConfiguracao ===
            updatedMenu.IdIntegracaoConfiguracao
          )
            return { ...integration, ...updatedMenu };
          else return integration;
        });

        setIntegrations(newMenuList);
      }
    },
    [integrations]
  );

  const fetchList = async () => {
    return await Api.get(
      "list-configuration-integration",
      "adminportal",
      "",
      true
    )
      .then(({ Success, Result }) => {
        if (Success) {
          setIntegrations(Result);
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "105",
          screen: "Configuração de Integrações",
          problem: `Erro na chamada do endpoint list-configuration-integration - ${error}`,
        });
      })
      .finally(() => setSearching(false));
  };

  const fetchSystemList = async () => {
    return await Api.get("list-system-integration", "adminportal", "", true)
      .then(({ Success, Result }) => {
        if (Success) {
          setSystemIntegrations(Result);
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "105",
          screen: "Configuração de Integrações",
          problem: `Erro na chamada do endpoint list-system-integration - ${error}`,
        });
      });
  };

  const handleRefresh = async () => {
    setSearching(true);

    await fetchSystemList();
    return await fetchList();
  };

  useEffect(() => {
    fetchSystemList();
    fetchList();
    setLocalStorageActions({
      nomeTela: "Configuração de Integrações",
      idMenu: "105",
      acao: "Acessou o menu Configuração de Integrações",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <HeaderClientes
        section="Configuração de Integrações"
        subsection="Administração Portal"
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <ToolkitTable
                keyField="IdIntegracaoConfiguracao"
                dataTable={searching ? loadingItems : integrations}
                columnModel={getTableColumns(
                  setSelectedIntegration,
                  toggleFormDialog,
                  searching
                )}
                search
                allowEmpty
                CustomHeader={({ searchProps }) => (
                  <Grid
                    container
                    className="pl-4"
                    style={{ padding: isMobile ? "0 24px 10px" : "0 24px" }}
                  >
                    <Grid item xs={isMobile ? 12 : 4}>
                      <Box
                        component={Typography}
                        variant="h2"
                        marginBottom="0!important"
                      >
                        <Box component="span">Lista de Integrações</Box>
                        <IconButtonLoader
                          size="medium"
                          extraStyle={{
                            width: 24,
                            height: 24,
                            marginLeft: "0.3rem",
                          }}
                          title="Recarregar lista"
                          onClick={handleRefresh}
                          icon={<RefreshIcon color="primary" />}
                          aria-label="refresh"
                          color="primary"
                          disabled={searching}
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={isMobile ? 12 : 8}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: isMobile ? "flex-start" : "flex-end",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <SearchBar
                        className="form-control"
                        placeholder="Pesquisar Integração"
                        {...searchProps}
                        disabled={searching}
                      />
                      <Box
                        justifyContent="flex-end"
                        display="flex"
                        flexWrap="wrap"
                        style={{ marginLeft: isMobile ? 0 : 15 }}
                      >
                        <Button
                          variant="contained"
                          classes={{
                            root: classes.buttonRootWhite,
                          }}
                          onClick={() => toggleFormDialog("create")}
                          disabled={searching}
                        >
                          <Box
                            component={AddIcon}
                            position="relative"
                            top="-2px"
                          />{" "}
                          Criar
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                pagination={{
                  options: [25, 50, 100],
                  defaultOption: 25,
                }}
                noResultTitle="Não há integrações registradas"
                noResultSubtitle="Registre uma integração clicando em Criar"
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
      {Boolean(openFormDialog) && (
        <FormIntegrationDialog
          open
          formType={openFormDialog}
          systemIntegrations={systemIntegrations}
          onClose={(integration) => {
            setSelectedIntegration(null);
            if (integration?.IdIntegracaoConfiguracao > 0) {
              if (openFormDialog === "create") {
                const newMenuList = [...integrations, { ...integration }];

                setIntegrations(newMenuList);
              } else {
                updateList(openFormDialog, integration);
              }
            }
            setOpenFormDialog(null);
          }}
          selectedIntegration={selectedIntegration}
        />
      )}
    </>
  );
}

ConfiguracaoIntegracoes.propTypes = {};

export default ConfiguracaoIntegracoes;
